import { useCallback, useMemo } from 'react';
import {
    type SeriesInstanceUid,
    seriesInstanceUidOf,
} from '~/domain/dicom/uids';
import usePrimaryImage from '~/hooks/image/usePrimaryImage';
import { PrimaryImageControls } from '~/viewportGrid';

/**
 * The primary image as the viewport grid controls it. Choosing a loaded
 * image makes it the primary image through the same switch as the sidebar's
 * image list; choosing the current primary image changes nothing.
 */
export function usePrimaryImageControls(): PrimaryImageControls {
    const { availableImages, primaryImage, handleChangePrimaryImage } =
        usePrimaryImage(true);

    const choose = useCallback(
        (seriesInstanceUid: SeriesInstanceUid) => {
            if (
                seriesInstanceUid ===
                seriesInstanceUidOf(primaryImage?.seriesUid)
            ) {
                return;
            }
            // Only a loaded image is shown by the grid, so only a loaded
            // image is chosen here.
            const picked = availableImages.find(
                (image) =>
                    image.imageObject !== undefined &&
                    seriesInstanceUidOf(image.seriesInstanceUID) ===
                        seriesInstanceUid
            );
            if (picked) {
                void handleChangePrimaryImage(picked);
            }
        },
        [availableImages, handleChangePrimaryImage, primaryImage?.seriesUid]
    );

    return useMemo(() => ({ choose }), [choose]);
}
